import './validate-invite.screen.scss';
import {useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Typography, Box} from '@mui/material';
import {PublicContentPage} from '../../../../components/public-content-page/public-content-page';
import {useHistory} from 'react-router-dom';
import {translations} from '../../../../i18n/translations';
import {AppContextType} from '../../../../contexts/app/types';
import {AppContext} from '../../../../contexts/app/context';
import {SnackBarContextType} from '../../../../contexts/snack-bar/types';
import {SnackBarContext} from '../../../../contexts/snack-bar/context';
import {getBrandName} from '../../../../utils/functions/get-product-name';
import {InvitesRepository} from '../../../../repositories/invites';
import {ScreenPaths} from '../../../../enums/screen-path';
import {TextInput} from '../../../../components/text-input/text-input';
import {InviteType} from '../../../../backend/enums/invite-type';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LoadingButton from '@mui/lab/LoadingButton';

export default function ValidateInviteScreen() {
  // Get context
  const appContext: AppContextType = useContext(AppContext);
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);

  // Translations
  const {t} = useTranslation();

  // Get history
  let history = useHistory();

  // Hold state
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');
  const [codeError, setCodeError] = useState<string | undefined>();
  const [showCodeError, setShowCodeError] = useState<boolean>(false);

  // Validation
  useEffect(() => {
    // Code validation
    if (code.trim().length > 0) {
      // No errors
      if (codeError !== undefined) {
        setCodeError(undefined);
      }

      // Enable button
      if (buttonDisabled !== false) {
        setButtonDisabled(false);
      }
    } else {
      // Error
      if (showCodeError) {
        setCodeError(t(translations.validate_invite.errors.form_validation_invalid_field));
      }

      // Disable button
      if (buttonDisabled !== true) {
        setButtonDisabled(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, showCodeError]);

  // Validate invitation
  const handleValidateInvitation = async (event: any) => {
    event.preventDefault();

    // Reset form errors
    setCodeError(undefined);

    // Everything looks ok, let's perform the request. Start loading.
    setSubmitting(true);

    try {
      // Send request
      const result = await InvitesRepository.instance.getInviteByVoucher<any>(code.trim());

      // Everything went well. Go to the invite screen.
      if (result.type === InviteType.Relationship) {
        history.replace({
          pathname: ScreenPaths.AcceptRelationshipInvite,
          search: `?voucherCode=${code.trim()}`,
        });
      } else if (result.type === InviteType.Affiliation) {
        history.replace({
          pathname: ScreenPaths.AcceptAffiliationInvite,
          search: `?voucherCode=${code.trim()}`,
        });
      } else {
        // Error
        throw new Error('Invite type not implemented.');
      }
    } catch (error) {
      // Nothing went well
      snackBarContext.setPublicToastNotification({
        message: t(translations.validate_invite.errors.invalid_invite_error_message),
        severity: 'error',
      });
    }

    // Stop loading
    setSubmitting(false);
  };

  // Handle change code input
  const handleChangeCode = (value: string | undefined | null) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Set code with the new value
    setCode(finalValue);
  };

  // Handle blur code input
  const handleBlurCode = () => {
    if (!showCodeError) {
      setShowCodeError(true);
    }
  };

  return (
    <PublicContentPage>
      <Typography variant="h6" sx={{fontWeight: 'bold'}} className="validate-invite-title">
        {t(translations.validate_invite.title)}
      </Typography>
      <Typography variant="body1" className="validate-invite-instructions">
        {t(translations.validate_invite.instructions_1, {
          brand: getBrandName(appContext.state.configuration.product),
        })}
      </Typography>
      <Typography variant="body1" className="validate-invite-instructions">
        {t(translations.validate_invite.instructions_2, {
          brand: getBrandName(appContext.state.configuration.product),
        })}
      </Typography>
      <form onSubmit={handleValidateInvitation}>
        <Box className="validate-invite-input">
          <TextInput
            label={t(translations.validate_invite.code_input)}
            value={code}
            handleChange={handleChangeCode}
            handleBlur={handleBlurCode}
            disabled={submitting}
            error={codeError}
            required={true}
            type="text"
          />
        </Box>
        <LoadingButton
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          size="medium"
          loading={submitting}
          loadingPosition="end"
          endIcon={<ArrowForwardIcon />}
          disabled={buttonDisabled}
        >
          {t(translations.validate_invite.send_button)}
        </LoadingButton>
      </form>
    </PublicContentPage>
  );
}
